import { useState } from 'react';
import { cn } from '../../lib/utils';
import { hashString, initials } from '../../../shared/utils';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps {
  name?: string | null;
  email?: string | null;
  src?: string | null;
  size?: AvatarSize;
  /** Square with a small radius — used for workspace/brand marks. */
  shape?: 'circle' | 'square';
  className?: string;
}

const SIZES: Record<AvatarSize, string> = {
  xs: 'size-6 text-[10px]',
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-14 text-base',
  xl: 'size-20 text-xl',
};

/**
 * Deterministic tint per identity so a member list reads as distinct faces even
 * without photos. Hues are spaced around the wheel; saturation stays low enough
 * to sit inside the neutral UI.
 */
function tintStyle(seed: string): { backgroundColor: string; color: string } {
  const hue = hashString(seed.toLowerCase()) % 360;
  return {
    backgroundColor: `oklch(0.62 0.11 ${hue} / 0.22)`,
    color: `oklch(0.72 0.13 ${hue})`,
  };
}

export function Avatar({ name, email, src, size = 'sm', shape = 'circle', className }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const label = name || email || '?';
  const showImage = Boolean(src) && !failed;

  return (
    <span
      title={label}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center overflow-hidden',
        'font-semibold ring-1 ring-border',
        shape === 'circle' ? 'rounded-full' : 'rounded-lg',
        SIZES[size],
        className,
      )}
      style={showImage ? undefined : tintStyle(label)}
    >
      {showImage ? (
        <img
          src={src ?? undefined}
          alt={label}
          loading="lazy"
          onError={() => setFailed(true)}
          className="size-full object-cover"
        />
      ) : (
        <span aria-hidden>{initials(label)}</span>
      )}
    </span>
  );
}

export interface AvatarGroupProps {
  people: Array<{ id: string; name?: string | null; email?: string | null; avatarUrl?: string | null }>;
  max?: number;
  size?: AvatarSize;
  className?: string;
}

/** Overlapping stack with a "+N" overflow chip — member lists and post authors. */
export function AvatarGroup({ people, max = 4, size = 'xs', className }: AvatarGroupProps) {
  const shown = people.slice(0, max);
  const overflow = people.length - shown.length;
  return (
    <span className={cn('inline-flex items-center -space-x-1.5', className)}>
      {shown.map((person) => (
        <Avatar
          key={person.id}
          name={person.name}
          email={person.email}
          src={person.avatarUrl}
          size={size}
          className="ring-2 ring-surface"
        />
      ))}
      {overflow > 0 && (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-elevated font-medium text-muted ring-2 ring-surface',
            SIZES[size],
          )}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}
